const SERVER_IDS = ['server1', 'server2', 'server3', 'server4', 'server5'];
const EMPTY_SLOT = { status: 'EMPTY', command: '—', ballot: '—' };
const BALLOT_ONE = '[1, server1]';

const slot = (status, command = '—', ballot = '—') => ({ status, command, ballot });
const cloneNodes = (nodes) => nodes.map((node) => ({
    ...node,
    log: node.log.map((entry) => ({ ...entry })),
}));

const makeNode = (id, {
    leader = 'Server 1', role = 'follower', health = 'online', chosen = false, promised = BALLOT_ONE,
} = {}) => ({
    id,
    label: id.replace('server', 'Server '),
    role,
    health,
    ballot: role === 'leader' || role === 'candidate' ? BALLOT_ONE : '[0, —]',
    promised,
    leader,
    executed: chosen ? 1 : 0,
    log: [chosen ? slot('CHOSEN', 'PUT x = 1', BALLOT_ONE) : { ...EMPTY_SLOT }, { ...EMPTY_SLOT }, { ...EMPTY_SLOT }],
});

const makeCluster = ({ election = false, chosen = false } = {}) => SERVER_IDS.map((id) => makeNode(id, {
    chosen,
    leader: election ? 'Unknown' : 'Server 1',
    role: id === 'server1' ? (election ? 'candidate' : 'leader') : 'follower',
    promised: election && id !== 'server1' ? '[0, —]' : BALLOT_ONE,
}));

const makeExecutedCluster = () => makeCluster().map((node) => ({
    ...node,
    executed: 3,
    log: [
        slot('CHOSEN', 'PUT x = 1', BALLOT_ONE),
        slot('CHOSEN', 'APPEND x += 2', BALLOT_ONE),
        slot('CHOSEN', 'GET x', BALLOT_ONE),
    ],
}));

function setSlot(node, slotNumber, value) {
    while (node.log.length < slotNumber) node.log.push({ ...EMPTY_SLOT });
    node.log[slotNumber - 1] = { ...node.log[slotNumber - 1], ...value };
}

function buildFrames(initialNodes, opening, steps) {
    let nodes = cloneNodes(initialNodes);
    const frames = [{ ...opening, id: 'initial', nodes: cloneNodes(nodes) }];

    steps.forEach((step, index) => {
        const next = cloneNodes(nodes);
        if (step.addNode) next.push(makeNode(step.addNode.id, step.addNode.options));
        (step.updates || []).forEach(({ node: nodeId, set, slot: slotUpdate }) => {
            const node = next.find(({ id }) => id === nodeId);
            if (!node) return;
            if (set) Object.assign(node, set);
            if (slotUpdate) setSlot(node, slotUpdate.number, slotUpdate.value);
        });
        nodes = next;
        frames.push({ ...step, id: `${index + 1}-${step.event}`, nodes: cloneNodes(nodes) });
    });

    return frames;
}

const acceptSlotOne = (node, command = 'PUT x = 1') => ({
    node,
    slot: { number: 1, value: slot('ACCEPTED', command, BALLOT_ONE) },
});
const chooseSlotOne = (node, command = 'PUT x = 1') => ({
    node,
    set: { executed: 1 },
    slot: { number: 1, value: slot('CHOSEN', command, BALLOT_ONE) },
});

const stableLeader = {
    id: 'stable', label: 'Reach agreement', eyebrow: 'Start here',
    description: 'Follow one write from client request to a chosen, replicated log entry.',
    frames: buildFrames(makeCluster({ election: true }), {
        phase: 'Before any messages', event: 'PaxosServer.init()', type: 'START', quorum: '0 / 3',
        summary: 'Five servers start with empty logs. Any three form a majority.',
        detail: 'Each PaxosServer plays proposer, acceptor, and replica. Server 1 will try to become the stable leader so later writes can skip repeated elections.',
        invariant: 'Nothing can be chosen without support from at least three of five servers.', handler: 'PaxosServer.init',
    }, [
        {
            phase: '1 · Ask to lead', event: 'ProposeLeaderRequest', type: 'PREPARE', quorum: '1 / 3', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'Server 1 asks every peer to recognize ballot [1, server1].',
            detail: 'This is Phase 1A. A promise means the peer will reject lower ballots and return anything it already accepted.',
            invariant: 'Ballots impose one global order on competing leaders.', handler: 'handleProposeLeaderRequest',
            updates: SERVER_IDS.slice(1).map((node) => ({ node, set: { promised: BALLOT_ONE } })),
        },
        {
            phase: '2 · Collect promises', event: 'ProposeLeaderReply', type: 'PROMISE', quorum: '2 / 3', from: 'server2', to: ['server1'],
            summary: 'Server 2 promises the ballot. Two votes are visible, but that is not a majority yet.',
            detail: 'Server 1 counts its own vote plus Server 2. With five members it must wait for one more promise.',
            invariant: 'Two of five servers cannot make a decision.', handler: 'handleProposeLeaderReply',
        },
        {
            phase: '2 · Collect promises', event: 'ProposeLeaderReply', type: 'PROMISE', quorum: '3 / 3', from: 'server3', to: ['server1'],
            summary: 'Server 3 supplies the third vote. Server 1 is now the stable leader.',
            detail: 'The three-vote quorum intersects every other possible three-vote quorum. That overlap is the core safety property.',
            invariant: 'Any future majority includes at least one server from this majority.', handler: 'handleProposeLeaderReply',
            updates: [{ node: 'server1', set: { role: 'leader', leader: 'Server 1' } }],
        },
        {
            phase: '3 · Propose a write', event: 'PaxosRequest', type: 'CLIENT', quorum: '1 / 3', from: 'client', to: ['server1'],
            summary: 'The client asks to PUT x = 1. The leader records it as ACCEPTED in slot 1.',
            detail: 'ACCEPTED means proposed locally; it is not safe to execute yet. The client request is wrapped with at-most-once metadata.',
            invariant: 'An accepted command is tentative until a majority accepts it.', handler: 'handlePaxosRequest',
            updates: [acceptSlotOne('server1')],
        },
        {
            phase: '4 · Replicate', event: 'AcceptCommandRequest', type: 'ACCEPT', quorum: '1 / 3', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'The leader sends slot 1, its command, and its ballot to all four followers.',
            detail: 'This is Phase 2A. Each follower writes the accepted value to its own local log before replying.',
            invariant: 'A slot and ballot always travel with the command.', handler: 'handleAcceptCommandRequest',
            updates: SERVER_IDS.slice(1).map((node) => acceptSlotOne(node)),
        },
        {
            phase: '5 · Count accepts', event: 'AcceptCommandReply', type: 'ACCEPTED', quorum: '2 / 3', from: 'server2', to: ['server1'],
            summary: 'Server 2 accepts. The leader still needs one more vote.',
            detail: 'The leader counts itself and Server 2. Replies from Servers 4 and 5 can be late or lost without changing safety.',
            invariant: 'A partial quorum never executes a command.', handler: 'handleAcceptCommandReply',
        },
        {
            phase: '5 · Reach majority', event: 'AcceptCommandReply', type: 'CHOSEN', quorum: '3 / 3', from: 'server3', to: ['server1'],
            summary: 'The third acceptance makes slot 1 CHOSEN. The leader can execute it.',
            detail: 'CHOSEN is permanent: every later leader must preserve this value when it merges promise replies.',
            invariant: 'No different command can ever be chosen for slot 1.', handler: 'handleAcceptCommandReply',
            updates: [chooseSlotOne('server1')],
        },
        {
            phase: '6 · Catch followers up', event: 'PingRequest', type: 'HEARTBEAT', quorum: '3 / 3', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'A heartbeat carries the chosen log entry to every follower.',
            detail: 'mergeLog prefers CHOSEN entries. Each follower then runs executeCommands in slot order and reaches the same application state.',
            invariant: 'All healthy replicas execute the same ordered commands.', handler: 'handlePingRequest',
            updates: SERVER_IDS.slice(1).map((node) => chooseSlotOne(node)),
        },
        {
            phase: '7 · Reply', event: 'PaxosReply', type: 'RESULT', quorum: '3 / 3', from: 'server1', to: ['client'],
            summary: 'The client receives OK after the chosen command executes.',
            detail: 'At-most-once metadata lets a retry return the saved result rather than running PUT twice.',
            invariant: 'The client observes one linearizable operation.', handler: 'PaxosClient.handlePaxosReply',
        },
    ]),
};

const packetLoss = {
    id: 'loss', label: 'Drop a packet', eyebrow: 'Retry',
    description: 'See why packet loss delays a write but does not create conflicting decisions.',
    frames: buildFrames(makeCluster(), {
        phase: 'Ready', event: 'Stable leader', type: 'START', quorum: '0 / 3',
        summary: 'Server 1 is leader. The five-server cluster needs three votes.',
        detail: 'The normal path is ready, but the network may delay or discard any individual message.',
        invariant: 'Three healthy servers are enough to keep working.', handler: 'PaxosServer',
    }, [
        {
            phase: 'Client write', event: 'PaxosRequest', type: 'CLIENT', quorum: '1 / 3', from: 'client', to: ['server1'],
            summary: 'The leader proposes PUT theme = dark in slot 1.',
            detail: 'The local acceptance contributes the first of three required votes.', invariant: 'The command is not executable yet.', handler: 'handlePaxosRequest',
            updates: [acceptSlotOne('server1', 'PUT theme = dark')],
        },
        {
            phase: 'Packet loss', event: 'AcceptCommandRequest', type: 'DROPPED', quorum: '1 / 3', from: 'server1', to: SERVER_IDS.slice(1), droppedTargets: ['server2'],
            summary: 'Server 2 misses the request. Servers 3–5 receive it normally.',
            detail: 'Messages are independent. A single dropped packet does not erase the leader’s proposal or affect the other deliveries.',
            invariant: 'Loss changes timing, not which values are legal.', handler: 'network',
            updates: ['server3', 'server4', 'server5'].map((node) => acceptSlotOne(node, 'PUT theme = dark')),
        },
        {
            phase: 'One reply', event: 'AcceptCommandReply', type: 'ACCEPTED', quorum: '2 / 3', from: 'server3', to: ['server1'],
            summary: 'Server 3 replies. The cluster is one vote short of choosing.',
            detail: 'The leader keeps the slot ACCEPTED while it waits. It must not guess that a majority will eventually respond.',
            invariant: 'Two votes cannot choose in a five-member configuration.', handler: 'handleAcceptCommandReply',
        },
        {
            phase: 'Retry after 25 ms', event: 'AcceptCommandTimer', type: 'TIMER', quorum: '2 / 3', from: 'server1', to: ['server2'],
            summary: 'The per-peer timer resends the exact same Phase 2 request to Server 2.',
            detail: 'The retry keeps the original slot, command, and ballot. It stops once that peer replies or the slot becomes chosen.',
            invariant: 'Retransmission is idempotent.', handler: 'onAcceptCommandTimer',
            updates: [acceptSlotOne('server2', 'PUT theme = dark')],
        },
        {
            phase: 'Majority reached', event: 'AcceptCommandReply', type: 'CHOSEN', quorum: '3 / 3', from: 'server2', to: ['server1'],
            summary: 'The retry succeeds. Three votes make the command CHOSEN.',
            detail: 'The leader executes the value once; extra replies can arrive later without changing the decision.',
            invariant: 'Duplicate requests and replies do not duplicate execution.', handler: 'handleAcceptCommandReply',
            updates: [chooseSlotOne('server1', 'PUT theme = dark')],
        },
        {
            phase: 'Catch-up', event: 'PingRequest', type: 'HEARTBEAT', quorum: '3 / 3', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'The next heartbeat tells every follower that slot 1 is chosen.',
            detail: 'A follower can learn the final decision even if it missed an earlier protocol message.',
            invariant: 'Healthy replicas eventually converge after communication recovers.', handler: 'handlePingRequest',
            updates: SERVER_IDS.slice(1).map((node) => chooseSlotOne(node, 'PUT theme = dark')),
        },
    ]),
};

const leaderFailure = {
    id: 'failover', label: 'Crash the leader', eyebrow: 'Failover',
    description: 'Remove the active leader and elect another without losing the chosen log prefix.',
    frames: buildFrames(makeCluster({ chosen: true }), {
        phase: 'Ready', event: 'Stable cluster', type: 'START', quorum: '3 / 3',
        summary: 'All five replicas have executed slot 1. Server 1 is leader.',
        detail: 'Followers expect periodic PingRequest heartbeats from the current leader.',
        invariant: 'Every replica agrees that PUT x = 1 is chosen.', handler: 'PaxosServer',
    }, [
        {
            phase: 'Crash', event: 'Leader unavailable', type: 'FAULT', quorum: '0 / 3', from: 'server1', to: [],
            summary: 'Server 1 crashes. Four live servers remain.',
            detail: 'The failure stops progress briefly, but it does not erase the chosen value stored by the other replicas.',
            invariant: 'One crash still leaves enough servers for a majority.', handler: 'network',
            updates: [{ node: 'server1', set: { role: 'offline', health: 'offline' } }],
        },
        {
            phase: 'Detect silence', event: 'PingCheckTimer', type: 'TIMER', quorum: '1 / 3', from: 'server2', to: ['server2'],
            summary: 'Server 2 misses heartbeats and starts election ballot [2, server2].',
            detail: 'The timer is only a suspicion mechanism. The new server still needs Phase 1 promises before it can lead.',
            invariant: 'False suspicion cannot bypass quorum rules.', handler: 'onPingCheckTimer',
            updates: [{ node: 'server2', set: { role: 'candidate', ballot: '[2, server2]', promised: '[2, server2]' } }],
        },
        {
            phase: 'Ask to lead', event: 'ProposeLeaderRequest', type: 'PREPARE', quorum: '1 / 3', from: 'server2', to: ['server3', 'server4', 'server5'],
            summary: 'Server 2 asks the three reachable followers to promise its higher ballot.',
            detail: 'Each promise will include that server’s local log, including the already chosen slot 1.',
            invariant: 'A new leader must learn the prior accepted history.', handler: 'handleProposeLeaderRequest',
            updates: ['server3', 'server4', 'server5'].map((node) => ({ node, set: { promised: '[2, server2]' } })),
        },
        {
            phase: 'First promise', event: 'ProposeLeaderReply', type: 'PROMISE', quorum: '2 / 3', from: 'server3', to: ['server2'],
            summary: 'Server 3 promises and returns its chosen log. One more vote is required.',
            detail: 'Server 2 merges the log immediately, but does not declare leadership with only two votes.',
            invariant: 'The chosen prefix is carried into the new ballot.', handler: 'handleProposeLeaderReply',
        },
        {
            phase: 'New leader', event: 'ProposeLeaderReply', type: 'PROMISE', quorum: '3 / 3', from: 'server4', to: ['server2'],
            summary: 'Server 4 provides the majority. Server 2 becomes leader.',
            detail: 'The new leader begins from the same chosen prefix and can now accept new client commands.',
            invariant: 'Leadership changes; chosen history does not.', handler: 'handleProposeLeaderReply',
            updates: [
                { node: 'server2', set: { role: 'leader', leader: 'Server 2' } },
                { node: 'server3', set: { leader: 'Server 2' } }, { node: 'server4', set: { leader: 'Server 2' } },
                { node: 'server5', set: { leader: 'Server 2' } },
            ],
        },
        {
            phase: 'New write', event: 'PaxosRequest', type: 'CLIENT', quorum: '1 / 3', from: 'client', to: ['server2'],
            summary: 'The client sends PUT y = 2 to the replacement leader.',
            detail: 'Server 2 proposes the write in slot 2, after the preserved slot 1.',
            invariant: 'New operations extend rather than rewrite the chosen prefix.', handler: 'handlePaxosRequest',
            updates: [{ node: 'server2', slot: { number: 2, value: slot('ACCEPTED', 'PUT y = 2', '[2, server2]') } }],
        },
        {
            phase: 'Live quorum', event: 'AcceptCommandRequest + replies', type: 'CHOSEN', quorum: '3 / 3', from: 'server2', to: ['server3', 'server4'],
            summary: 'Server 2 learns that a majority accepted slot 2 and marks it CHOSEN.',
            detail: 'Servers 3 and 4 are locally ACCEPTED until the leader disseminates the decision. Server 5 can catch up later.',
            invariant: 'The system tolerates up to two unavailable servers out of five.', handler: 'handleAcceptCommandReply',
            updates: [
                { node: 'server2', set: { executed: 2 }, slot: { number: 2, value: slot('CHOSEN', 'PUT y = 2', '[2, server2]') } },
                { node: 'server3', slot: { number: 2, value: slot('ACCEPTED', 'PUT y = 2', '[2, server2]') } },
                { node: 'server4', slot: { number: 2, value: slot('ACCEPTED', 'PUT y = 2', '[2, server2]') } },
            ],
        },
        {
            phase: 'Follower catch-up', event: 'PingRequest', type: 'HEARTBEAT', quorum: '3 / 3', from: 'server2', to: ['server3', 'server4', 'server5'],
            summary: 'The new leader propagates slot 2 to every surviving follower.',
            detail: 'When Server 1 returns, this Lab 3 implementation has no durable crash-recovery protocol; that is a documented limitation.',
            invariant: 'The live replicas converge on the same two-slot history.', handler: 'handlePingRequest',
            updates: ['server3', 'server4', 'server5'].map((node) => ({ node, set: { executed: 2 }, slot: { number: 2, value: slot('CHOSEN', 'PUT y = 2', '[2, server2]') } })),
        },
    ]),
};

const networkPartition = {
    id: 'partition', label: 'Split the network', eyebrow: 'Partition',
    description: 'Separate two servers from three and see why only the majority side can make progress.',
    frames: buildFrames(makeCluster(), {
        phase: 'Ready', event: 'Connected cluster', type: 'START', quorum: '0 / 3',
        summary: 'All five servers can communicate. Server 1 is leader.',
        detail: 'The cluster has one five-member configuration and a majority threshold of three.',
        invariant: 'Every decision needs three votes from this fixed membership.', handler: 'PaxosServer',
    }, [
        {
            phase: 'Network splits 3–2', event: 'Partition', type: 'FAULT', quorum: '0 / 3', from: null, to: [],
            summary: 'Servers 4 and 5 become isolated from Servers 1–3.',
            detail: 'Both sides may still be running. The protocol must prevent the two-server side from deciding a conflicting value.',
            invariant: 'Only the three-server side contains a majority.', handler: 'network',
            updates: ['server4', 'server5'].map((node) => ({ node, set: { health: 'isolated' } })),
        },
        {
            phase: 'Majority receives write', event: 'PaxosRequest', type: 'CLIENT', quorum: '1 / 3', from: 'client', to: ['server1'],
            summary: 'The client’s PUT region = east reaches the leader on the majority side.',
            detail: 'Server 1 accepts locally and sends Phase 2 only to the peers it can currently reach.',
            invariant: 'The majority side may continue safely.', handler: 'handlePaxosRequest',
            updates: [acceptSlotOne('server1', 'PUT region = east')],
        },
        {
            phase: 'Majority votes', event: 'AcceptCommandRequest + replies', type: 'CHOSEN', quorum: '3 / 3', from: 'server1', to: ['server2', 'server3'],
            summary: 'Server 1 learns the three-vote quorum and marks the value CHOSEN.',
            detail: 'Servers 2 and 3 have accepted it but learn the CHOSEN status from later leader traffic. The isolated replicas are not required.',
            invariant: 'A 3–2 split permits progress on exactly one side.', handler: 'handleAcceptCommandReply',
            updates: [chooseSlotOne('server1', 'PUT region = east'), acceptSlotOne('server2', 'PUT region = east'), acceptSlotOne('server3', 'PUT region = east')],
        },
        {
            phase: 'Minority receives request', event: 'PaxosRequest', type: 'BLOCKED', quorum: '2 / 3', from: 'client', to: ['server4'],
            summary: 'The two-server minority cannot choose a competing write.',
            detail: 'Even if Servers 4 and 5 suspect the leader, two promises or acceptances are below the configured quorum.',
            invariant: 'The minority sacrifices availability to preserve consistency.', handler: 'handlePaxosRequest',
        },
        {
            phase: 'Network heals', event: 'PingRequest', type: 'RECOVER', quorum: '3 / 3', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'Communication returns and the isolated servers catch up to the chosen log.',
            detail: 'The leader heartbeat transfers the decision. Followers prefer the CHOSEN slot and execute it in order.',
            invariant: 'All five replicas converge after the partition heals.', handler: 'handlePingRequest',
            updates: SERVER_IDS.slice(1).map((node) => ({ ...chooseSlotOne(node, 'PUT region = east'), set: { executed: 1, health: 'online', leader: 'Server 1' } })),
        },
    ]),
};

const membershipChange = {
    id: 'membership', label: 'Add a server', eyebrow: 'Boundary',
    description: 'See why joining a Paxos replica is a protocol change, not just another network connection.',
    frames: buildFrames(makeCluster({ chosen: true }), {
        phase: 'Fixed configuration', event: 'Five-member cluster', type: 'START', quorum: '3 / 3',
        summary: 'Lab 3 starts every PaxosServer with the same fixed five-address array.',
        detail: 'That array determines message recipients and the majority calculation. It does not change while the group runs.',
        invariant: 'All participants must agree on exactly who counts as a voter.', handler: 'PaxosServer.servers',
    }, [
        {
            phase: 'Operator request', event: 'Server 6 appears', type: 'JOIN?', quorum: '3 / 3', from: null, to: [],
            summary: 'A sixth process comes online with an empty log—but it is not a group member.',
            detail: 'The existing servers do not know its address, will not send it Paxos messages, and will not count its vote.',
            invariant: 'A process cannot add itself to a consensus membership.', handler: 'No Lab 3 handler exists',
            addNode: { id: 'server6', options: { role: 'not-member', health: 'joining', leader: 'Unknown' } },
        },
        {
            phase: 'Request rejected', event: 'No membership protocol', type: 'UNSUPPORTED', quorum: '3 / 3', from: 'server6', to: ['server1'],
            summary: 'Lab 3 has no AddServer message or safe reconfiguration step.',
            detail: 'Simply changing some nodes to a six-member quorum while others still use five creates conflicting quorum rules and can break safety.',
            invariant: 'Membership itself must be agreed upon through consensus.', handler: 'PaxosServer(Address, Address[], Application)',
            updates: [{ node: 'server6', set: { role: 'not-member', health: 'rejected' } }],
        },
        {
            phase: 'What production needs', event: 'Joint consensus', type: 'DESIGN', quorum: 'old + new', from: null, to: [],
            summary: 'A real implementation would commit a transition from the old set to the new set.',
            detail: 'Typical reconfiguration uses a joint phase recognized by both memberships, transfers the chosen log or state, then commits the final membership. Lab 3 intentionally omits this.',
            invariant: 'No two configurations may independently choose conflicting values.', handler: 'Not implemented in Lab 3',
        },
        {
            phase: 'Lab 4 distinction', event: 'ShardMaster.Join', type: 'LAB 4', quorum: 'group-level', from: null, to: [],
            summary: 'Lab 4 can add a replica group and move shards—but still cannot grow one Paxos group in place.',
            detail: 'ShardMaster Join changes which fixed replica group owns each shard. The Lab 4 handout explicitly says the peer set inside a Paxos group cannot evolve.',
            invariant: 'Adding a group is different from adding one voter to a Paxos group.', handler: 'ShardMaster.execute(Join)',
        },
    ]),
};

const garbageCollection = {
    id: 'garbage-collection', label: 'Compact the log', eyebrow: 'Memory',
    description: 'Track every replica’s executed prefix before safely clearing old Paxos slots.',
    frames: buildFrames(makeExecutedCluster(), {
        phase: 'Three executed slots', event: 'Chosen prefix', type: 'START', quorum: '5 / 5 progress reports',
        summary: 'Every replica has chosen and executed slots 1–3, but none may discard them yet.',
        detail: 'Local execution is insufficient evidence for garbage collection because a lagging replica may still need the chosen commands to catch up.',
        invariant: 'A slot remains available until every configured replica has executed it.', handler: 'firstNonCleared / executedCommandIndex',
    }, [
        {
            phase: 'Advertise progress', event: 'PingRequest', type: 'HEARTBEAT', quorum: '1 / 5 reports', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'The leader heartbeat asks followers to report their latest executed slot.',
            detail: 'The heartbeat also repairs missing decisions before a follower reports progress.',
            invariant: 'Execution progress is monotonic and refers to a contiguous CHOSEN prefix.', handler: 'handlePingRequest',
        },
        {
            phase: 'Collect follower progress', event: 'PingReply', type: 'PROGRESS', quorum: '3 / 5 reports', from: 'server3', to: ['server1'],
            summary: 'The leader has progress from itself and two followers, but cannot clear yet.',
            detail: 'This lab’s simplified compaction rule waits for all configured servers—not merely a decision quorum.',
            invariant: 'A majority is enough to choose; it is not enough for this garbage-collection scheme.', handler: 'handlePingReply',
        },
        {
            phase: 'Slow replica responds', event: 'PingReply', type: 'PROGRESS', quorum: '5 / 5 reports', from: 'server5', to: ['server1'],
            summary: 'All five replicas report execution through slot 3.',
            detail: 'The minimum executed index across the membership is now 3, establishing a global deletion watermark.',
            invariant: 'Every server has incorporated the state represented by the cleared prefix.', handler: 'handlePingReply',
        },
        {
            phase: 'Publish watermark', event: 'GarbageCollectionRequest', type: 'COMPACT', quorum: '5 / 5 reports', from: 'server1', to: SERVER_IDS.slice(1),
            summary: 'The leader tells replicas they may clear slots 1–3.',
            detail: 'Only protocol metadata is removed; the deterministic application state produced by those commands remains.',
            invariant: 'Compaction cannot erase state needed by any configured replica.', handler: 'GarbageCollectionRequest',
            updates: SERVER_IDS.flatMap((node) => [
                { node, slot: { number: 1, value: slot('CLEARED') } },
                { node, slot: { number: 2, value: slot('CLEARED') } },
                { node, slot: { number: 3, value: slot('CLEARED') } },
            ]),
        },
        {
            phase: 'Advance firstNonCleared', event: 'Log compaction complete', type: 'CLEARED', quorum: '5 / 5 reports', from: null, to: [],
            summary: 'The cleared prefix is no longer returned as accepted or chosen log data.',
            detail: 'In a production majority-based compactor, a lagging replica would instead require a state snapshot; this lab deliberately avoids that extra protocol.',
            invariant: 'Safety survives memory reclamation because every member already executed the prefix.', handler: 'firstNonCleared',
        },
    ]),
};

export const paxosScenarios = [stableLeader, packetLoss, leaderFailure, networkPartition, garbageCollection, membershipChange];
